import { supabase } from '../supabase/config';

// Confirm and remove participants
export const confirmRemoveParticipants = async (eventId) => {
  const selectedParticipants = Array.from(window.selectedParticipantsToRemove);
  
  if (selectedParticipants.length === 0) return;
  
  const supabaseClient = window.supabase || supabase;
  
  try {
    // Get current event data
    const { data: eventData, error: fetchError } = await supabaseClient
      .from('events')
      .select('participants, title')
      .eq('event_id', eventId)
      .single();
    
    if (fetchError) throw fetchError;
    
    // Remove selected participants
    const participantIdsToRemove = selectedParticipants.map(p => p.id);
    const updatedParticipants = eventData.participants.filter(
      id => !participantIdsToRemove.includes(id)
    );
    
    // Update event in database
    const { error: updateError } = await supabaseClient
      .from('events')
      .update({ participants: updatedParticipants })
      .eq('event_id', eventId);
    
    if (updateError) throw updateError;
    
    // Create notifications for removed participants
    for (const participant of selectedParticipants) {
      await supabaseClient
        .from('notifications')
        .insert({
          user_id: participant.id,
          type: 'removed_from_event',
          title: 'Removed from Event',
          message: `You were removed from "${eventData.title}"`,
          event_id: eventId,
          event_title: eventData.title
        });
    }
    
    // Update notification counts for removed users
    if (window.updateNotificationCount) {
      window.updateNotificationCount();
    }
    
// Close confirmation modal
    document.getElementById('removeConfirmationModal').remove();
    
    // Close participants panel first
    const participantsPanel = document.getElementById('allParticipantsPanel');
    if (participantsPanel) {
      participantsPanel.remove();
    }
    
    // Update event details panel with fresh data
    const eventDetailsPanel = document.getElementById('eventDetailsPanel');
    if (eventDetailsPanel && eventDetailsPanel.dataset.eventId === eventId) {
      // Wait a bit for database to update, then refresh
      setTimeout(async () => {
        await window.updateEventDetailsPanel(eventId);
      }, 200);
    }
    
// Update marker inline instead of refreshing (prevents duplicates)
    setTimeout(async () => {
      const supabaseClient = window.supabase || supabase;
      const { data: markerData } = await supabaseClient
        .from('events')
        .select('place_id, participants, people_limit')
        .eq('event_id', eventId)
        .single();
      
      if (markerData) {
        const placeId = markerData.place_id;
        if (placeId && window.eventMarkersRef.current) {
          const marker = window.eventMarkersRef.current.find(m => {
            const content = m.content || m.getElement?.() || m._element;
            if (!content) return false;
            const directPlaceId = content.dataset?.placeId;
            const childPlaceId = content.querySelector?.('[data-place-id]')?.dataset?.placeId;
            const markerPlaceId = directPlaceId || childPlaceId;
            return markerPlaceId === placeId;
          });
          
          if (marker) {
            const markerElement = marker.content || marker.getElement?.() || marker._element;
            if (markerElement) {
              const participantCountSpans = markerElement.querySelectorAll('span[style*="font-weight: 600"]');
              participantCountSpans.forEach(countSpan => {
                if (countSpan.textContent.includes('/')) {
                  const newCount = markerData.participants.length;
                  const limit = markerData.people_limit;
                  countSpan.textContent = limit ? `${newCount} / ${limit}` : `${newCount} / ∞`;
                }
              });
            }
          }
        }
      }
    }, 300);
    
    // Reload user events
    if (window.loadUserEvents) {
      setTimeout(() => {
        window.loadUserEvents();
      }, 400);
    }
    
    // Show success message
    window.showToast(
      `${selectedParticipants.length} participant${selectedParticipants.length > 1 ? 's' : ''} removed successfully`,
      'success'
    );
    
  } catch (error) {
    window.showToast('Failed to remove participants. Please try again.', 'error');
  }
};

export const showAllParticipantsList = async (eventData) => {
  const supabaseClient = window.supabase || supabase;
  const participantIds = eventData.participants || [eventData.creatorId];
  const participantsData = [];

  for (const participantId of participantIds) {
    try {
      const { data, error } = await supabaseClient
        .from('users').select('id, name, profile_picture, online_status, last_seen')
        .eq('id', participantId).single();
      if (!error && data) {
        const isOnline = data.online_status === 'online' && data.last_seen &&
          (new Date() - new Date(data.last_seen)) < 120000;
        participantsData.push({
          ...data, isOnline,
          isOrganizer: participantId === eventData.creatorId,
          isCurrentUser: participantId === (window.currentAppUser?.uid || window.currentUser?.uid)
        });
      }
    } catch (err) { console.error('Error fetching participant:', err); }
  }

  participantsData.sort((a, b) => {
    if (a.isOrganizer) return -1; if (b.isOrganizer) return 1;
    if (a.isCurrentUser) return -1; if (b.isCurrentUser) return 1;
    return 0;
  });

  window.dispatchEvent(new CustomEvent('openAllParticipantsPanel', {
    detail: { eventData, participantsData }
  }));
};

// Function to close all participants panel
export const closeAllParticipantsPanel = () => {
  window.dispatchEvent(new CustomEvent('closeAllParticipantsPanel'));
};

export const showAllParticipantsListFresh = async (eventId) => {
  const supabaseClient = window.supabase || supabase;
  
  // Fetch fresh event data
  const { data: eventData, error } = await supabaseClient
    .from('events')
    .select('*')
    .eq('event_id', eventId)
    .single();
  
  if (error || !eventData) {
    return;
  }
  
  // Format and pass to existing function
  const formattedEvent = {
    eventId: eventData.event_id,
    title: eventData.title,
    description: eventData.description,
    placeId: eventData.place_id,
    placeName: eventData.place_name,
    placeAddress: eventData.place_address,
    placePhotoUrl: eventData.place_photo_url,
    latitude: eventData.latitude,
    longitude: eventData.longitude,
    startDateTime: eventData.start_date_time,
    endDateTime: eventData.end_date_time,
    peopleLimit: eventData.people_limit,
    creatorId: eventData.creator_id,
    createdBy: eventData.created_by,
    participants: eventData.participants,
    customLabel: eventData.custom_label,
      requireApprove: eventData.require_approve,
      pendingRequests: eventData.pending_requests,
      archived: eventData.archived || false,
    };
  
  window.showAllParticipantsList(formattedEvent);
};

window.confirmRemoveParticipants = confirmRemoveParticipants;
window.showAllParticipantsList = showAllParticipantsList;
window.closeAllParticipantsPanel = closeAllParticipantsPanel;
window.showAllParticipantsListFresh = showAllParticipantsListFresh;